import {
  CARD_SIZE,
  COLUMNS,
  FREE_INDEX,
  cardColumnIndex,
  emptyCard,
  evaluateClaim,
  missingChipLabel,
  type CardCell,
  type Modality,
} from '@bingo/shared';
import { useMemo, useRef, useState } from 'react';

/**
 * Conferência da cartela — porte do modal do protótipo.
 *
 * O veredito mostrado aqui vem de `evaluateClaim` do @bingo/shared, a MESMA
 * função que o servidor roda ao gravar. O operador vê em tempo real o que o
 * servidor vai decidir; não existe caminho em que a tela diga uma coisa e o
 * banco registre outra.
 *
 * A digitação segue o protótipo: dois dígitos avançam sozinhos, Enter avança
 * com um dígito, Backspace em campo vazio volta.
 */

interface ClaimDialogProps {
  drawn: readonly number[];
  modality: Modality;
  freeCenter: boolean;
  initialCardNumber: string | null;
  onResolve: (input: {
    cells: CardCell[];
    decision: 'CONFIRM' | 'FORCE' | 'REJECT' | 'CANCEL';
    cardNumber: string | null;
  }) => void;
  busy: boolean;
}

export function ClaimDialog({
  drawn,
  modality,
  freeCenter,
  initialCardNumber,
  onResolve,
  busy,
}: ClaimDialogProps) {
  const [cells, setCells] = useState<CardCell[]>(emptyCard);
  const [cardNumber, setCardNumber] = useState(initialCardNumber ?? '');
  const [useFreeCenter, setUseFreeCenter] = useState(freeCenter);

  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const evaluation = useMemo(
    () => evaluateClaim({ cells, drawn, modality, freeCenter: useFreeCenter }),
    [cells, drawn, modality, useFreeCenter],
  );

  const isFree = (index: number) => useFreeCenter && index === FREE_INDEX;

  const focusCell = (from: number, direction: 1 | -1) => {
    for (let i = from + direction; i >= 0 && i < CARD_SIZE; i += direction) {
      if (!isFree(i)) {
        inputs.current[i]?.focus();
        inputs.current[i]?.select();
        return;
      }
    }
  };

  const setCell = (index: number, raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '').slice(0, 2);

    setCells((previous) => {
      const next = [...previous];
      next[index] = digits ? Number(digits) : null;
      return next;
    });

    if (digits.length === 2) focusCell(index, 1);
  };

  const verdictClass =
    evaluation.verdict === 'BINGO'
      ? 'vb-ok'
      : evaluation.verdict === 'INVALID' || evaluation.verdict === 'NOT_YET'
        ? 'vb-no'
        : 'vb-wait';

  return (
    <div className="overlay">
      <form
        className="modal chk"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          if (!evaluation.canConfirm) return;
          onResolve({ cells, decision: 'CONFIRM', cardNumber: cardNumber.trim() || null });
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'end',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2>Conferência da cartela</h2>
            <p className="sub" style={{ margin: '6px 0 0' }}>
              {drawn.length} bolas sorteadas
            </p>
          </div>
          <label style={{ width: 170 }}>
            Nº da cartela
            <input
              value={cardNumber}
              onChange={(event) => setCardNumber(event.target.value)}
              inputMode="numeric"
            />
          </label>
        </div>

        <div className="chk-grid">
          <div className="card">
            {COLUMNS.map((column) => (
              <div
                key={column.letter}
                className="h"
                style={{ '--c': column.color } as React.CSSProperties}
              >
                {column.letter}
              </div>
            ))}

            {Array.from({ length: CARD_SIZE }, (_, index) => {
              const column = COLUMNS[cardColumnIndex(index)]!;
              const status = evaluation.cellStatus[index];
              const free = isFree(index);
              const winning = evaluation.winningIndices.includes(index);

              const classes = ['cc'];
              if (free) classes.push('free');
              if (status === 'BAD') classes.push('bad');
              if (status === 'HIT') classes.push('hit');
              if (status === 'MISS') classes.push('miss');
              if (winning) classes.push('win');

              return (
                <input
                  key={index}
                  ref={(element) => {
                    inputs.current[index] = element;
                  }}
                  className={classes.join(' ')}
                  inputMode="numeric"
                  maxLength={2}
                  disabled={free}
                  placeholder={free ? 'LIVRE' : ''}
                  value={free ? '' : (cells[index] ?? '')}
                  aria-label={`${column.letter}, linha ${Math.floor(index / 5) + 1}`}
                  style={{ '--c': column.color } as React.CSSProperties}
                  onChange={(event) => setCell(index, event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      focusCell(index, 1);
                    } else if (event.key === 'Backspace' && !event.currentTarget.value) {
                      event.preventDefault();
                      focusCell(index, -1);
                    }
                  }}
                />
              );
            })}
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            <div className={`verdict-box ${verdictClass}`} aria-live="polite">
              <div className="vt">{evaluation.title}</div>
              <div>{evaluation.detail}</div>

              {evaluation.missingIndices.length > 0 ? (
                <div className="chips">
                  {evaluation.missingIndices.map((index) => (
                    <span key={index} className="chip n">
                      {missingChipLabel(cells, index)}
                    </span>
                  ))}
                </div>
              ) : null}

              {evaluation.warning ? <div className="warn">{evaluation.warning}</div> : null}
            </div>

            <label className="check">
              <input
                type="checkbox"
                checked={useFreeCenter}
                onChange={(event) => setUseFreeCenter(event.target.checked)}
              />
              Centro livre (casa sem número)
            </label>

            <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13, lineHeight: 1.45 }}>
              Digite os números como estão na cartela, linha por linha. Com 2 dígitos o campo avança
              sozinho; com 1 dígito, use Enter.
            </p>
          </div>
        </div>

        <div className="actions" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn" onClick={() => setCells(emptyCard())}>
            Limpar
          </button>

          <div className="actions">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                onResolve({ cells, decision: 'CANCEL', cardNumber: cardNumber.trim() || null })
              }
            >
              Fechar
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                onResolve({ cells, decision: 'REJECT', cardNumber: cardNumber.trim() || null })
              }
            >
              Não é bingo · continuar jogo
            </button>
            {!evaluation.canConfirm && evaluation.filledCount > 1 ? (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() =>
                  onResolve({ cells, decision: 'FORCE', cardNumber: cardNumber.trim() || null })
                }
              >
                Confirmar mesmo assim
              </button>
            ) : null}
            <button type="submit" className="btn primary" disabled={!evaluation.canConfirm || busy}>
              Bingo confirmado · registrar ganhador
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
